import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ListChecks, CheckCircle, MinusCircle, XCircle, Circle, Loader2, ChevronDown,
} from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';

const STATUS_META = {
  NOT_STARTED: { label: 'Not started', icon: Circle, cls: 'text-muted-foreground' },
  COMPLETED: { label: 'Completed', icon: CheckCircle, cls: 'text-green-600' },
  NOT_APPLICABLE: { label: 'N/A', icon: MinusCircle, cls: 'text-slate-500' },
  UNABLE_TO_COMPLETE: { label: 'Unable', icon: XCircle, cls: 'text-red-600' },
};

const SETTABLE = ['COMPLETED', 'NOT_APPLICABLE', 'UNABLE_TO_COMPLETE', 'NOT_STARTED'];

/**
 * Day 18 — per-shift support-duty checklist. Status updates queue via the
 * offline action queue (CHECKLIST_UPDATE) and sync when online. Marking a task
 * "Unable to complete" requires a note.
 */
export default function MobileShiftChecklist({ shiftId, readOnly }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [openItem, setOpenItem] = useState(null);     // item id whose menu is open
  const [unableFor, setUnableFor] = useState(null);   // item pending an "unable" note
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mobile/shifts/${shiftId}/checklist`);
      setItems(res.data?.data?.items || []);
    } catch (_) {
      // graceful: leave empty
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const applyStatus = async (item, status, notes) => {
    setSaving(true);
    // optimistic update
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status, notes: notes || null } : i));
    try {
      await queueOfflineAction(ACTION_TYPES.CHECKLIST_UPDATE, {
        shiftId, itemId: item.id, status, notes: notes || undefined,
      });
      if (isOnline()) {
        const res = await syncAllActions();
        if (res.error) toast.error('Saved offline — will retry');
        else toast.success('Checklist updated');
      } else {
        toast.success('Saved — will sync when online');
      }
    } finally {
      setSaving(false);
      setOpenItem(null);
      setUnableFor(null);
      setNote('');
    }
  };

  const handleSelect = (item, status) => {
    if (status === 'UNABLE_TO_COMPLETE') {
      setUnableFor(item);
      setNote('');
      setOpenItem(null);
      return;
    }
    applyStatus(item, status);
  };

  const completedCount = items.filter((i) => i.status === 'COMPLETED' || i.status === 'NOT_APPLICABLE').length;

  return (
    <Card data-testid="shift-checklist">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">Task Checklist</p>
          </div>
          {items.length > 0 && (
            <Badge variant="secondary" className="text-xs" data-testid="checklist-progress">{completedCount}/{items.length}</Badge>
          )}
        </div>

        {loading ? (
          <p className="text-xs text-muted-foreground">Loading checklist…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground">No checklist for this shift.</p>
        ) : (
          <ul className="space-y-1">
            {items.map((item) => {
              const meta = STATUS_META[item.status] || STATUS_META.NOT_STARTED;
              const Icon = meta.icon;
              const isOpen = openItem === item.id;
              return (
                <li key={item.id} className="rounded-lg" data-testid="checklist-item">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 py-2 px-1 disabled:opacity-60"
                    disabled={readOnly || saving}
                    onClick={(e) => { e.stopPropagation(); setOpenItem(isOpen ? null : item.id); }}
                    data-testid={`checklist-toggle-${item.itemKey}`}
                  >
                    <Icon className={`h-5 w-5 shrink-0 ${meta.cls}`} />
                    <span className="flex-1 text-left text-sm">{item.label}</span>
                    <span className={`text-xs ${meta.cls}`}>{meta.label}</span>
                    {!readOnly && <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                  </button>
                  {item.notes && (
                    <p className="text-[11px] text-muted-foreground pl-8 pb-1">Note: {item.notes}</p>
                  )}
                  {isOpen && !readOnly && (
                    <div className="grid grid-cols-2 gap-1 pl-8 pb-2" data-testid={`checklist-options-${item.itemKey}`}>
                      {SETTABLE.map((s) => {
                        const sm = STATUS_META[s];
                        const active = item.status === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={saving}
                            className={`flex items-center justify-start text-xs h-8 px-2 rounded-md border transition-colors disabled:opacity-50 ${
                              active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted border-input'
                            }`}
                            onClick={(e) => { e.stopPropagation(); handleSelect(item, s); }}
                            data-testid={`checklist-set-${item.itemKey}-${s.toLowerCase()}`}
                          >
                            <sm.icon className="h-3.5 w-3.5 mr-1" /> {sm.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* Unable-to-complete note (required) */}
      <Dialog open={!!unableFor} onOpenChange={(v) => { if (!v) setUnableFor(null); }}>
        <DialogContent data-testid="unable-note-dialog">
          <DialogHeader>
            <DialogTitle>Why couldn't this be completed?</DialogTitle>
            <DialogDescription className="text-xs">A note is required to mark "{unableFor?.label}" as unable to complete.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Explain why the task could not be completed"
            rows={3}
            data-testid="unable-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnableFor(null)} disabled={saving}>Cancel</Button>
            <Button
              disabled={saving || !note.trim()}
              onClick={() => applyStatus(unableFor, 'UNABLE_TO_COMPLETE', note.trim())}
              data-testid="unable-note-confirm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
